/**
 * Glass-harmonica synthesis, in about a hundred lines of WebAudio.
 *
 * Every sound is built from the same tuned scale, so the whole game is in key
 * and any two blends are consonant with each other. Pitch is derived from the
 * *colour*: hue picks the scale degree, lightness picks the octave. A dark mix
 * sounds low and dull, a bright one rings. The player hears their own colour.
 *
 * The context is created lazily on the first gesture, because mobile browsers
 * refuse to start audio any earlier.
 */

const BASE = 220; // A3
/** Minor pentatonic. No semitone clashes, so nothing ever sounds wrong. */
const SCALE = [0, 3, 5, 7, 10];

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;
let ready = false;

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  master = ctx.createGain();
  master.gain.value = 0.5;
  master.connect(ctx.destination);
  return ctx;
}

/** Call from a real user gesture. Safe to call repeatedly. */
export function unlock(): void {
  const c = audio();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  if (!ready) {
    // A single silent sample is enough to open the audio session on iOS.
    const src = c.createBufferSource();
    src.buffer = c.createBuffer(1, 1, c.sampleRate);
    src.connect(c.destination);
    src.start(0);
    ready = true;
  }
}

export function setMuted(next: boolean): void {
  muted = next;
  if (master && ctx) master.gain.setTargetAtTime(next ? 0 : 0.5, ctx.currentTime, 0.02);
}

export function isMuted(): boolean {
  return muted;
}

/** Hue → a degree on the pentatonic scale, so the whole wheel stays in key. */
function degreeFor(hue: number): number {
  const norm = ((hue % 360) + 360) % 360;
  return SCALE[Math.round(norm / 72) % SCALE.length];
}

function freqFor(hue: number, lightness01: number, octaveSpan = 2): number {
  const degree = degreeFor(hue);
  // Lightness maps to octave in half steps, so a pale mix rings and a deep one
  // sits low without ever leaving the scale.
  const octave = Math.round((1 - lightness01) * octaveSpan * 2) / 2;
  return BASE * Math.pow(2, degree / 12 + octave - 1);
}

interface ToneOptions {
  freq: number;
  dur?: number;
  gain?: number;
  type?: OscillatorType;
  /** Detuned second voice, in cents. Gives the glass its shimmer. */
  detune?: number;
  attack?: number;
  delay?: number;
}

function tone(o: ToneOptions): void {
  const c = audio();
  if (!c || !master || muted) return;
  const t = c.currentTime + (o.delay ?? 0);
  const dur = o.dur ?? 0.5;
  const peak = (o.gain ?? 0.3) * 0.5;

  const voice = (detune: number, mult: number, level: number) => {
    const osc = c.createOscillator();
    osc.type = o.type ?? 'sine';
    osc.frequency.value = o.freq * mult;
    osc.detune.value = detune;

    const amp = c.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(Math.max(peak * level, 0.0002), t + (o.attack ?? 0.006));
    amp.gain.exponentialRampToValueAtTime(0.0001, t + dur);

    osc.connect(amp).connect(master!);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  };

  voice(0, 1, 1);
  voice(-(o.detune ?? 6), 1.002, 0.5);
  voice(0, 2.01, 0.18);
}

/** A short filtered noise burst: the "tick" of glass touching glass. */
function tick(delay = 0, q = 6, freq = 2600, gain = 0.1): void {
  const c = audio();
  if (!c || !master || muted) return;
  const len = Math.floor(c.sampleRate * 0.05);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = freq;
  filter.Q.value = q;
  const amp = c.createGain();
  amp.gain.value = gain;
  src.connect(filter).connect(amp).connect(master);
  src.start(c.currentTime + delay);
}

function chord(freqs: number[], dur: number, gain: number, spread = 0.075): void {
  freqs.forEach((f, i) => tone({ freq: f, dur, gain, delay: i * spread, detune: 4 }));
}

// ---------------------------------------------------------------------------
// The kit
// ---------------------------------------------------------------------------

export function sTap(): void {
  tone({ freq: 1180, dur: 0.14, gain: 0.14, type: 'triangle' });
  tick(0, 9, 3200, 0.05);
}

export function sSelect(): void {
  tone({ freq: 880, dur: 0.2, gain: 0.18, type: 'triangle' });
}

export function sDeselect(): void {
  tone({ freq: 660, dur: 0.16, gain: 0.13, type: 'triangle' });
}

/** Called with the new colour's hue and lightness, so the mix sings its own note. */
export function sBlend(hue: number, lightness01: number): void {
  const f = freqFor(hue, lightness01);
  tone({ freq: f, dur: 0.9, gain: 0.34, detune: 9 });
  tone({ freq: f * 1.5, dur: 0.6, gain: 0.12, delay: 0.03 });
  tick(0, 5, 2200, 0.07);
}

export function sUndo(): void {
  tone({ freq: 480, dur: 0.24, gain: 0.16, type: 'triangle' });
  tone({ freq: 360, dur: 0.3, gain: 0.12, delay: 0.05, type: 'triangle' });
}

export function sSpend(): void {
  tick(0, 14, 4200, 0.09);
  tone({ freq: 1420, dur: 0.1, gain: 0.1, type: 'triangle' });
}

/** Wins climb. A perfect lands a fifth above; anything worse stops lower. */
export function sWin(grade: 'perfect' | 'excellent' | 'great' | 'close'): void {
  const root = 392; // G4
  const steps: Record<typeof grade, number[]> = {
    perfect: [0, 4, 7, 12, 16],
    excellent: [0, 4, 7, 12],
    great: [0, 4, 7],
    close: [0, 3, 7],
  };
  const dur = grade === 'perfect' ? 1.5 : 1.1;
  chord(
    steps[grade].map((s) => root * Math.pow(2, s / 12)),
    dur,
    0.26,
    grade === 'perfect' ? 0.08 : 0.06,
  );
}

export function sMiss(): void {
  chord([220, 261.6], 1.2, 0.22, 0.05);
  tone({ freq: 138, dur: 1.4, gain: 0.2, type: 'sine' });
}

export function sReveal(): void {
  tone({ freq: 1568, dur: 1.1, gain: 0.16 });
  tick(0.02, 3, 5200, 0.06);
}

/** Submitting: a lift, so the wait before the verdict has a shape. */
export function sSubmit(): void {
  tone({ freq: 330, dur: 0.5, gain: 0.2, type: 'triangle' });
  tone({ freq: 494, dur: 0.6, gain: 0.16, delay: 0.06, type: 'triangle' });
  tick(0.1, 4, 3800, 0.06);
}

export function sStreak(): void {
  [523.25, 659.25, 783.99].forEach((f, i) =>
    tone({ freq: f, dur: 0.8, gain: 0.2, delay: i * 0.07 }),
  );
}

export function sError(): void {
  tone({ freq: 196, dur: 0.22, gain: 0.18, type: 'square' });
}
